'use strict';

const DEV_MOCK = location.hostname === 'localhost' || location.hostname === '127.0.0.1';

// The build is identical for both Firebase targets, so the backend is chosen at
// runtime by the host we're served from: the staging site (mp-games-staging.*)
// talks to the staging API — matching that site's CSP connect-src — and every
// other deployed host talks to prod.
const IS_STAGING_HOST = /(^|\.)mp-games-staging(\.|$)/.test(location.hostname)
  || location.hostname.includes('staging');

const API_BASE = DEV_MOCK        ? 'https://partners-api.staging.muslimpro.com/v1'
               : IS_STAGING_HOST ? 'https://partners-api.staging.muslimpro.com/v1'
               :                   'https://partners-api.muslimpro.com/v1';

const GAME_CONFIG = {
  // Single mode for now; the existing /game/session/start `mode` field
  // needs a value, and we want a stable string for analytics.
  mode:             'standard',

  // ── Kahoot-style flat run ────────────────────────────────────────────
  // The player picks a level up front; the level only sets the answer-timer
  // length (below). One flat run of `questionsPerRun` mixed-difficulty
  // questions is served — no tiers, no gating. The pool is the same across
  // levels (drawn by merging all `difficulties`), so difficulty == time
  // pressure, not content.
  levels: {
    beginner:     35,                 // answer-timer seconds per question
    intermediate: 25,
    expert:       15,
  },
  defaultLevel:     'beginner',

  // Question pool is built by merging these difficulties from the backend
  // /game/questions endpoint, then shuffling. Used only for the fetch — the
  // player never sees a difficulty label during play.
  difficulties:     ['easy', 'medium', 'hard'],
  questionsPerRun:  7,

  // Read window: when a question appears we show the prompt alone with a
  // draining "loading" bar (Kahoot-style) before revealing the tiles + answer
  // timer. The length scales with the prompt so short questions don't drag and
  // long ones get a fair read — clamped at both ends (a fixed long window is
  // boring on short prompts). This window does NOT count toward the speed score.
  readMsPerWord:    450,
  readMinMs:        2500,
  readMaxMs:        6500,

  streakBonusAt:    3,

  // ── Kahoot speed-curve scoring ───────────────────────────────────────
  // Points are dominated by how fast you answer within the answer phase: an
  // instant correct answer scores `maxPoints`, decaying linearly to half that
  // at the last moment. A streak bonus stacks on top, capped.
  //
  // KEEP IN SYNC with the backend envelope in MP-Integration-Server
  // src/game/rules/game-configs.ts — GAME_CONFIGS['islamic-quiz']:
  //   modes.standard.maxPoints = 210   (per-session clamp on the sent score)
  //   dailyCap                 = 500   (per-user, per-game, per-UTC-day)
  // The backend does not recompute the score; it clamps whatever we send. So
  // anything we award above 210 is silently truncated on the server and the
  // player is shown a number they never actually receive.
  //
  // A perfect 7-question run is 7*20 speed + 60 streak = 200, which sits just
  // under the 210 ceiling. Answering everything correctly but at the last
  // instant is 130. Raising these past 210 requires raising maxPoints in
  // game-configs.ts in the same change.
  maxPoints:        20,
  streakStep:       3,                // bonus per streak step past the first
  streakCap:        5,                // max streak steps that count toward bonus
};

// Answer-timer length (ms) for the player's chosen level.
function answerTimeMs(s) {
  const secs = GAME_CONFIG.levels[s && s.level] ?? GAME_CONFIG.levels[GAME_CONFIG.defaultLevel];
  return secs * 1000;
}

// Read-window length (ms), scaled to the prompt's word count and clamped so it
// never feels sluggish on short questions or rushed on long ones.
function readTimeMs(q) {
  const words = (q && q.prompt) ? q.prompt.trim().split(/\s+/).length : 0;
  const ms    = words * GAME_CONFIG.readMsPerWord;
  return Math.min(GAME_CONFIG.readMaxMs, Math.max(GAME_CONFIG.readMinMs, ms));
}

// Kahoot-style score for one question. `responseMs` is time spent in the
// answer phase only (the read window is excluded). Faster → more points.
function computeQuestionScore(correct, responseMs, answerMs, streak) {
  if (!correct) return 0;
  const frac    = Math.min(1, Math.max(0, responseMs / answerMs));   // 0 instant … 1 last moment
  const speed   = Math.round(GAME_CONFIG.maxPoints * (1 - frac / 2)); // maxPoints → maxPoints/2
  const steps   = streak >= 2 ? Math.min(streak - 1, GAME_CONFIG.streakCap) : 0;
  const streakB = steps * GAME_CONFIG.streakStep;
  return speed + streakB;
}

'use strict';

// Gamification API — Pattern D (cookie auth, credentials: 'include').
// Identical contract to rami/puzzle. The only game-specific bits are the
// `game: 'islamic-quiz'` literal sent on start/submit, plus fetchTier() which
// pulls one difficulty tier of questions at a time from the public backend
// `/game/questions` endpoint.

const api = (() => {

  let _mockBalance = 1200;
  let _mockAuthed  = DEV_MOCK
    && new URLSearchParams(location.search).get('signedIn') === '1';

  const _mock = {
    getBalance() {
      if (!_mockAuthed) return Promise.reject(_err('/game/balance', 401));
      return Promise.resolve({ balance: _mockBalance, currency: 'pts' });
    },
    startSession(_mode) {
      if (!_mockAuthed) return Promise.reject(_err('/game/session/start', 401));
      return Promise.resolve({
        sessionId: 'dev-' + Math.random().toString(36).slice(2),
        balance:   _mockBalance,
        currency:  'pts',
      });
    },
    completeSession(_sessionId, _events, clientScore) {
      if (!_mockAuthed) return Promise.reject(_err('/game/session/complete', 401));
      const pts = Math.max(0, clientScore | 0);
      _mockBalance += pts;
      return Promise.resolve({ pointsEarned: pts, balance: _mockBalance, currency: 'pts' });
    },
    submitSession(_game, _mode, _events, clientScore) {
      if (!_mockAuthed) return Promise.reject(_err('/game/session/submit', 401));
      const pts = Math.max(0, clientScore | 0);
      _mockBalance += pts;
      return Promise.resolve({
        sessionId:    'dev-' + Math.random().toString(36).slice(2),
        pointsEarned: pts,
        balance:      _mockBalance,
        currency:     'pts',
      });
    },
    abandonSession() {
      return Promise.resolve({ ok: true });
    },
  };

  let _authQueue     = [];
  let _authRequested = false;

  function _awaitAuth() {
    return new Promise((resolve, reject) => {
      _authQueue.push({ resolve, reject });
      if (_authRequested) return;
      _authRequested = true;
      sendToHost('auth:login_required');
      if (DEV_MOCK) {
        setTimeout(() => { _mockAuthed = true; notifyAuthReady(); }, 600);
      }
    });
  }

  function notifyAuthReady() {
    const q = _authQueue; _authQueue = []; _authRequested = false;
    q.forEach(p => p.resolve());
  }

  function notifyAuthCancelled() {
    const q = _authQueue; _authQueue = []; _authRequested = false;
    q.forEach(p => p.reject(_err('auth:cancelled', 401)));
  }

  async function _withAuthRetry(fn) {
    try {
      return await fn();
    } catch (e) {
      if (e.status !== 401) throw e;
      await _awaitAuth();
      return fn();
    }
  }

  function _err(path, status) {
    const e = new Error(`API ${path} → ${status}`);
    e.status = status;
    return e;
  }

  async function _post(path, body) {
    const res = await fetch(API_BASE + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw _err(path, res.status);
    return res.json();
  }

  async function _get(path) {
    const res = await fetch(API_BASE + path, { credentials: 'include' });
    if (!res.ok) throw _err(path, res.status);
    return res.json();
  }

  function getBalance() {
    return DEV_MOCK ? _mock.getBalance() : _get('/game/balance');
  }

  function startSession(mode) {
    return _withAuthRetry(() =>
      DEV_MOCK ? _mock.startSession(mode)
               : _post('/game/session/start', { game: 'islamic-quiz', mode })
    );
  }

  function completeSession(sessionId, events, clientScore) {
    return _withAuthRetry(() =>
      DEV_MOCK ? _mock.completeSession(sessionId, events, clientScore)
               : _post('/game/session/complete', { sessionId, clientScore, events })
    );
  }

  function submitSession(mode, events, clientScore) {
    return _withAuthRetry(() =>
      DEV_MOCK ? _mock.submitSession('islamic-quiz', mode, events, clientScore)
               : _post('/game/session/submit', { game: 'islamic-quiz', mode, clientScore, events })
    );
  }

  function abandonSession(sessionId) {
    return DEV_MOCK ? _mock.abandonSession() : _post('/game/session/abandon', { sessionId });
  }

  function requestLogin() {
    return _awaitAuth();
  }

  // ── Tiered question fetch ─────────────────────────────────────────────
  // One tier (Easy/Medium/Hard) is fetched at a time — the next tier is only
  // requested once the player clears the current one (see quiz.js). The backend
  // `/game/questions` route samples `count` active questions of the given
  // difficulty from the question bank (it's public — guests reach it before
  // authenticating).
  function fetchTier(difficulty, count) {
    return _get(`/game/questions?game=islamic-quiz&difficulty=${encodeURIComponent(difficulty)}&count=${count}`);
  }

  return {
    getBalance, startSession, completeSession, submitSession, abandonSession,
    requestLogin, notifyAuthReady, notifyAuthCancelled, fetchTier,
  };
})();

'use strict';

const wallet = {
  balance:  0,
  currency: 'pts',
};

const _walletPill = () => document.getElementById('wallet-pill');

// Snapshot resume runs exactly once per page lifetime — auth:ready also
// re-runs initWallet to refresh the pill, but we must not double-restore.
let _bootResumeChecked = false;

async function initWallet() {
  try {
    const data = await api.getBalance();
    wallet.balance  = data.balance;
    wallet.currency = data.currency;
    state.isGuest   = false;
    _walletPill()?.classList.remove('hidden');
    renderWallet();
  } catch (e) {
    if (e.status === 401 || e.status === 403) {
      // Guest play: no cookie. Hide the wallet pill, let the user play.
      // Login is deferred until claim time.
      state.isGuest = true;
      _walletPill()?.classList.add('hidden');
    } else {
      // Network / unexpected error — keep wallet hidden, let later boots retry.
      wallet.balance = 0;
      renderWallet();
      return;
    }
  }

  if (!_bootResumeChecked) {
    _bootResumeChecked = true;
    maybeResumeFromSnapshot(!state.isGuest);
  }
}

function maybeResumeFromSnapshot(isAuthedNow) {
  const snap = snapshot.load();
  if (!snap)                       return;
  if (!snapshot.isFresh(snap))     { snapshot.clear(); return; }
  if (snap.v !== 1)                { snapshot.clear(); return; }
  if (snap.kind !== 'post-game')   return;

  restoreFromSnapshot(snap);

  if (isAuthedNow) {
    claimPoints();
  } else {
    showClaimCta();
  }
}

// Recovery for the boot race where native pre-injects the cookie but the
// WebView's first GET /game/balance fires before the cookie is committed.
function finishPendingSnapshotClaim() {
  if (!state.restoredFromSnapshot) return;
  if (state.isGuest)               return;
  if (!snapshot.load())            return;
  const claimBtn = document.getElementById('claimBtn');
  if (!claimBtn || claimBtn.disabled) return;
  claimPoints();
}

// Native → web entry point. bridge.js dispatches platform events here.
window.gameReceive = function (msg) {
  if (!msg || typeof msg !== 'object') return;
  switch (msg.type) {
    case 'wallet:update':
      wallet.balance  = msg.balance  ?? wallet.balance;
      wallet.currency = msg.currency ?? wallet.currency;
      renderWallet();
      break;
    case 'auth:ready':
      api.notifyAuthReady();
      initWallet().then(finishPendingSnapshotClaim);
      break;
    case 'auth:cancelled':
      api.notifyAuthCancelled();
      break;
    case 'game:pause':  pauseGame();  break;
    case 'game:resume': resumeGame(); break;
    case 'game:exit':   exitToMenu(); break;
  }
};

function renderWallet() {
  const el = document.getElementById('wallet-balance');
  if (el) el.textContent = `${wallet.balance} ${wallet.currency}`;
}

'use strict';

function readLS(k, def) {
  const v = localStorage.getItem(k);
  if (v === null) return def;
  return v === '1' || v === 'true';
}

const settings = {
  sound:        readLS('islamic-quiz.sound', true),
  haptic:       readLS('islamic-quiz.haptic', true),
  reduceMotion: readLS('islamic-quiz.reduceMotion', matchMedia('(prefers-reduced-motion: reduce)').matches),
};

function saveSettings() {
  localStorage.setItem('islamic-quiz.sound',        settings.sound ? '1' : '0');
  localStorage.setItem('islamic-quiz.haptic',       settings.haptic ? '1' : '0');
  localStorage.setItem('islamic-quiz.reduceMotion', settings.reduceMotion ? '1' : '0');
}

function haptic(pattern) {
  if (!settings.haptic) return;
  try { navigator.vibrate && navigator.vibrate(pattern); } catch (e) {}
}

'use strict';

// Resilient post-game claim snapshot — same contract as rami/puzzle.
// Persists state.events + display stats immediately before the guest claim
// flow hands off to native OAuth, so a process kill between auth:login_required
// and a successful /game/session/submit response leaves the user landing on the
// same end screen on next boot.

window.snapshot = (function () {
  const KEY    = 'islamic-quiz.snapshot.v1';
  const TTL_MS = 30 * 60 * 1000;          // 30 minutes
  const LEGACY_KEYS = [];

  try {
    for (const k of LEGACY_KEYS) localStorage.removeItem(k);
  } catch (_) {}

  function save(payload) {
    try {
      localStorage.setItem(KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn('[snapshot] save failed:', e && e.name);
    }
  }

  function load() {
    let raw;
    try { raw = localStorage.getItem(KEY); }
    catch (_) { return null; }
    if (raw == null) return null;
    try { return JSON.parse(raw); }
    catch (_) { clear(); return null; }
  }

  function clear() {
    try { localStorage.removeItem(KEY); } catch (_) {}
  }

  function isFresh(snap) {
    if (!snap || typeof snap.savedAt !== 'number' || typeof snap.ttlMs !== 'number') return false;
    return snap.savedAt + snap.ttlMs > Date.now();
  }

  // Pure projection of `state` → schema payload for the post-quiz case.
  function buildPostGame(s) {
    return {
      v:         1,
      kind:      'post-game',
      savedAt:   Date.now(),
      ttlMs:     TTL_MS,
      mode:      GAME_CONFIG.mode,
      isGuest:   !!s.isGuest,
      sessionId: s.sessionId || null,
      events:    Array.isArray(s.events) ? s.events.slice() : [],
      stats: {
        correct:     s.answers ? s.answers.filter(a => a.correct).length : 0,
        total:       s.answers ? s.answers.length : 0,
        bestStreak:  s.bestStreak  | 0,
        totalPoints: s.totalPoints | 0,
      },
    };
  }

  return { KEY, TTL_MS, save, load, clear, isFresh, buildPostGame };
})();

'use strict';

// Personal best — survives across sessions via localStorage, independent of
// the wallet (which only reflects *claimed*, server-credited points for authed
// users). This is a purely local "score to beat" so even guests have a goal.

window.personalBest = (function () {
  const KEY = 'islamic-quiz.pb.v1';

  function _empty() { return { points: 0, streak: 0, accuracy: 0, plays: 0 }; }

  function load() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return _empty();
      const v = JSON.parse(raw) || {};
      return {
        points:   v.points   | 0,
        streak:   v.streak   | 0,
        accuracy: v.accuracy | 0,
        plays:    v.plays    | 0,
      };
    } catch (_) { return _empty(); }
  }

  function save(pb) {
    try { localStorage.setItem(KEY, JSON.stringify(pb)); }
    catch (e) { console.warn('[pb] save failed:', e && e.name); }
  }

  // Fold a finished run into the stored bests. Returns { pb, beat } where
  // `beat` flags which records this run broke, so the end screen can celebrate.
  function record(run) {
    const pb   = load();
    const beat = {
      points:   (run.points   | 0) > pb.points,
      streak:   (run.streak   | 0) > pb.streak,
      accuracy: (run.accuracy | 0) > pb.accuracy,
    };
    pb.points   = Math.max(pb.points,   run.points   | 0);
    pb.streak   = Math.max(pb.streak,   run.streak   | 0);
    pb.accuracy = Math.max(pb.accuracy, run.accuracy | 0);
    pb.plays    = (pb.plays | 0) + 1;
    save(pb);
    return { pb, beat, isFirstPlay: pb.plays === 1 };
  }

  return { load, save, record };
})();

'use strict';

// Web Audio Sfx — synth-only (no asset files). Mirrors the pattern from
// rami/audio.js: lazy AudioContext, gated by settings.sound.

const Sfx = (() => {
  let ctx = null;

  function ensure() {
    if (ctx) return ctx;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = new AC();
    } catch (_) { ctx = null; }
    return ctx;
  }

  function tone(freq, durMs, type = 'sine', gain = 0.05) {
    if (!settings.sound) return;
    const c = ensure(); if (!c) return;
    const t = c.currentTime;
    const osc = c.createOscillator();
    const g   = c.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + durMs / 1000);
    osc.connect(g).connect(c.destination);
    osc.start(t);
    osc.stop(t + durMs / 1000 + 0.02);
  }

  // Punchy 3-note rising arpeggio for a correct answer (Kahoot-style ding).
  function correct() {
    tone(784,  90, 'triangle', 0.07);
    setTimeout(() => tone(1047, 90,  'triangle', 0.07), 80);
    setTimeout(() => tone(1568, 200, 'sine',     0.06), 160);
  }
  // Two-tone descending buzzer for a wrong answer / timeout.
  function wrong() {
    tone(330, 160, 'sawtooth', 0.06);
    setTimeout(() => tone(196, 260, 'sawtooth', 0.06), 120);
  }
  // Countdown tick — pitched up + louder while `urgent` (final seconds) so the
  // accelerating cadence reads as rising tension.
  function tick(urgent) {
    if (urgent) tone(990, 55, 'square', 0.035);
    else        tone(660, 40, 'square', 0.02);
  }
  function win() {
    tone(784,  110, 'triangle', 0.06);
    setTimeout(() => tone(988,  110, 'triangle', 0.06), 120);
    setTimeout(() => tone(1319, 110, 'triangle', 0.06), 240);
    setTimeout(() => tone(1568, 260, 'sine',     0.06), 360);
  }

  return { correct, wrong, tick, win, tone };
})();

'use strict';

const state = {
  screen:      'home',                // 'home' | 'game' | 'end'
  level:       null,                  // 'beginner' | 'intermediate' | 'expert' — chosen on the level-select screen
  questions:   [],                    // the whole run: [{ id, difficulty, prompt, options: [{id, text, isCorrect}] }]
  qIdx:        0,                      // index into questions (0..questionsPerRun-1)
  answers:     [],                    // session-wide: [{ qId, difficulty, selectedId, correct, msToAnswer }]
  totalPoints: 0,
  streak:      0,
  bestStreak:  0,

  // ── Per-question lifecycle ───────────────────────────────────────────
  phase:       'read',                // 'read' (prompt-only countdown) | 'answer' (tiles + timer)
  readTimer:   null,                  // setInterval handle for the read-window draining bar
  timer:       null,                  // setInterval handle for the answer-phase countdown
  readStartT:   0,                    // performance.now() when the read window began (shifted on pause/resume)
  answerStartT: 0,                    // performance.now() when the answer phase began (speed-score basis + answer timer ref)
  pausedAt:     0,                    // performance.now() when a pause began; 0 when not paused

  // Audit trail sent to backend on /session/complete or /session/submit.
  // Same shape as rami/puzzle, different event types.
  events:      [],

  running:     false,
  paused:      false,
  pendingAdvance: false,              // a correct-answer auto-advance that fired while paused; replayed on resume
  awaitingNext: false,                // true while a wrong/timeout answer is revealed and we wait for a manual "Next" tap

  // ── Gamification (Pattern D) ─────────────────────────────────────────
  sessionId:        null,
  sessionStartT:    0,
  isGuest:          true,             // true until /game/balance returns 200
  restoredFromSnapshot: false,
};

'use strict';

// Quiz engine — Kahoot-style. Each question runs in two phases:
//   READ   → the prompt is shown alone with a draining horizontal "loading"
//            bar (GAME_CONFIG.readTimeSec). Options are hidden. This window
//            does NOT count toward the speed score.
//   ANSWER → the four shape tiles appear and the answer timer (set by the
//            player's chosen level) starts. Fast correct answers score more.
// The run is flat: questionsPerRun mixed-difficulty questions, no tier gating.
// Events are pushed to state.events so /session/complete or /session/submit
// can log a full audit trail.

// Difficulty of the question on screen — taken from the question itself (used
// only for the audit trail; the player never sees it during play).
function questionDifficulty(q) {
  return (q && q.difficulty) || 'mixed';
}

function startQuestion(idx) {
  state.qIdx        = idx;
  state.phase       = 'read';
  state.awaitingNext = false;
  const q = state.questions[idx];
  if (!q) { finishGame(); return; }

  pushEvent('q_shown', { qId: q.id, difficulty: questionDifficulty(q) });
  renderQuestion(q, idx);              // paints prompt, hides options, shows read bar
  startReadCountdown();
}

// READ phase: a full-width bar drains from 100%→0% over readTimeSec, giving
// the player time to read the prompt before the options + answer timer appear.
function startReadCountdown() {
  const total  = readTimeMs(state.questions[state.qIdx]);
  state.readStartT = performance.now();   // on state so pause/resume can shift it
  if (state.readTimer) clearInterval(state.readTimer);

  renderReadProgress(1);               // full before first tick
  state.readTimer = setInterval(() => {
    if (state.paused) return;
    const remain = Math.max(0, total - (performance.now() - state.readStartT));
    renderReadProgress(remain / total);
    if (remain <= 0) {
      clearInterval(state.readTimer); state.readTimer = null;
      beginAnswerPhase();
    }
  }, 50);
}

// Transition READ → ANSWER: reveal the tiles and start the level-timed clock.
function beginAnswerPhase() {
  if (state.phase === 'answer' || !state.running) return;
  if (state.readTimer) { clearInterval(state.readTimer); state.readTimer = null; }
  const q = state.questions[state.qIdx];
  if (!q) { finishGame(); return; }

  state.phase       = 'answer';
  state.answerStartT = performance.now();
  revealOptions(q);
  startCountdown();
}

function startCountdown() {
  const total  = answerTimeMs(state);
  // Reference state.answerStartT (set in beginAnswerPhase) so a pause can give
  // the time back — and so it stays the single basis for the speed score.
  if (state.timer) clearInterval(state.timer);

  // Drive the SVG ring via stroke-dashoffset. 163.4 ≈ 2π·26 (circle r=26).
  const CIRC = 163.4;
  const totalSecs = Math.ceil(total / 1000);
  let lastTickSec = totalSecs + 1;                     // so the first whole second ticks
  renderCountdown(totalSecs, false);                   // paint full before first tick

  state.timer = setInterval(() => {
    if (state.paused) return;                          // mid-game pause (native bg or quit dialog)
    const elapsed = performance.now() - state.answerStartT;
    const remain  = Math.max(0, total - elapsed);
    const frac    = remain / total;
    const secs    = Math.ceil(remain / 1000);
    const urgent  = remain <= 5000;
    const bar = document.getElementById('countdown-ring-bar');
    if (bar) bar.style.strokeDashoffset = String(CIRC * (1 - frac));
    renderCountdown(secs, urgent);

    // Accelerating tick: one beep per whole second, doubling up in the final
    // 3s for Kahoot-style tension.
    if (secs !== lastTickSec && remain > 0) {
      lastTickSec = secs;
      Sfx.tick(urgent);
      if (remain <= 3000) setTimeout(() => Sfx.tick(true), 250);
    }

    if (remain <= 0) {
      clearInterval(state.timer); state.timer = null;
      onTimeout();
    }
  }, 50);
}

function stopCountdown() {
  if (state.timer)     { clearInterval(state.timer);     state.timer = null; }
  if (state.readTimer) { clearInterval(state.readTimer); state.readTimer = null; }
}

// Time spent in the answer phase so far, in ms (basis for the speed score).
function answerResponseMs() {
  return Math.round(performance.now() - state.answerStartT);
}

function submitAnswer(optionId) {
  if (!state.running || state.phase !== 'answer') return;
  const q = state.questions[state.qIdx];
  if (!q) return;
  stopCountdown();

  const chosen     = q.options.find(o => o.id === optionId);
  const correct    = !!(chosen && chosen.isCorrect);
  const msToAnswer = answerResponseMs();
  const difficulty = questionDifficulty(q);

  state.answers.push({ qId: q.id, difficulty, selectedId: optionId, correct, msToAnswer });
  pushEvent('q_answer', { qId: q.id, difficulty, selectedId: optionId, msToAnswer });

  if (correct) {
    state.streak += 1;
    if (state.streak > state.bestStreak) state.bestStreak = state.streak;
    const pts = computeQuestionScore(true, msToAnswer, answerTimeMs(state), state.streak);
    state.totalPoints += pts;
    showPointsGain(pts);
    Sfx.correct();
    haptic(20);
  } else {
    state.streak = 0;
    Sfx.wrong();
    haptic([10, 30, 10]);
  }

  revealOption(optionId, correct, q);
  updateScore();
  updateStreak();

  if (correct) {
    // Correct answers auto-advance, but linger long enough for the player to
    // enjoy the "+N pts" pop and the green reveal before moving on. If a quit
    // dialog paused us in the meantime, defer the advance until resume.
    setTimeout(() => {
      if (!state.running) return;
      if (state.paused) { state.pendingAdvance = true; return; }
      advance();
    }, 2100);
  } else {
    // Wrong answers stay on screen with the correct option revealed until the
    // learner taps "Next" — give them time to read the right answer. The
    // correct option is highlighted after a 500ms pause inside revealOption.
    waitForNext();
  }
}

function onTimeout() {
  if (!state.running) return;
  const q = state.questions[state.qIdx];
  if (!q) return;

  const difficulty = questionDifficulty(q);
  state.answers.push({ qId: q.id, difficulty, selectedId: null, correct: false, msToAnswer: answerTimeMs(state) });
  pushEvent('q_timeout', { qId: q.id, difficulty });

  state.streak = 0;
  Sfx.wrong();
  haptic([10, 30, 10]);

  revealCorrectOnly(q, 'timeout');

  updateStreak();
  // Same as a wrong answer: hold on the revealed answer until the learner
  // manually advances.
  waitForNext();
}

// Reveal the "Next question" button and pause the lifecycle until the learner
// taps it (wired in input.js → goNext). Idempotent per question.
function waitForNext() {
  state.awaitingNext = true;
  showNextButton();
}

// Called from the Next button / keyboard. Advances exactly once.
function goNext() {
  if (!state.awaitingNext) return;
  state.awaitingNext = false;
  hideNextButton();
  advance();
}

function advance() {
  const next = state.qIdx + 1;
  if (next < state.questions.length) {
    startQuestion(next);
    return;
  }
  finishGame();
}

function finishGame() {
  stopCountdown();
  state.running = false;
  Sfx.win();
  if (typeof endGame === 'function') endGame();
}

function pushEvent(type, data) {
  state.events.push({
    type,
    t: Math.round(performance.now() - state.sessionStartT),
    data: data || {},
  });
}

'use strict';

// Wires up all user-facing buttons. Called once from boot().

function bindInput() {
  // Play → level select (the player picks Beginner/Intermediate/Expert there).
  document.getElementById('btn-play')?.addEventListener('click', () => {
    showScreen('level');
  });

  // Level cards → start a run at the chosen difficulty (sets the answer timer).
  // Questions are fetched async, so show the shared full-screen loader (the same
  // gilded star used at boot) until startGame resolves and the game is painted.
  document.getElementById('level-options')?.addEventListener('click', async (e) => {
    if (document.getElementById('mp-loader')) return;     // a run is already starting
    const card = e.target.closest('.level-card');
    if (!card) return;
    const level = card.dataset.level;
    if (typeof startGame !== 'function') return;
    window.mpLoader && window.mpLoader.show();
    const shownAt = Date.now();
    try {
      await startGame(level);
    } finally {
      // Keep the loader up for a short minimum so a fast question fetch can't
      // flash-and-vanish before it's even perceived.
      const MIN_VISIBLE = 450;
      const elapsed = Date.now() - shownAt;
      if (elapsed < MIN_VISIBLE) {
        await new Promise((r) => setTimeout(r, MIN_VISIBLE - elapsed));
      }
      window.mpLoader && window.mpLoader.hide();
    }
  });

  // Leaving the quiz is handled by the native app's back button — the web
  // exposes no back/exit arrow. The end-screen "Menu" button (a forward
  // choice after finishing) still returns to the home screen.
  document.getElementById('btn-menu')?.addEventListener('click', () => {
    exitToMenu();
  });

  // Play again → back to level select so the player can re-pick difficulty.
  document.getElementById('btn-play-again')?.addEventListener('click', () => {
    showScreen('level');
  });

  document.getElementById('claimBtn')?.addEventListener('click', () => {
    if (typeof claimPoints === 'function') claimPoints();
  });

  // Option clicks — delegate from the #options container so re-rendered
  // buttons are picked up automatically.
  document.getElementById('options')?.addEventListener('click', (e) => {
    const btn = e.target.closest('.option');
    if (!btn || btn.disabled) return;
    submitAnswer(btn.dataset.optionId);
  });

  // Tap the question card during the read window to skip straight to the
  // answer phase (power-user shortcut; the read bar otherwise expires on its own).
  document.getElementById('question-card')?.addEventListener('click', () => {
    if (state.running && state.phase === 'read' && typeof beginAnswerPhase === 'function') {
      beginAnswerPhase();
    }
  });

  // Manual advance after a wrong answer / timeout.
  document.getElementById('btn-next')?.addEventListener('click', () => {
    if (typeof goNext === 'function') goNext();
  });

  // Keyboard nav: while awaiting Next, Enter/Space advances. In the answer
  // phase, 1–4 selects an option. During the read window, Enter/Space skips
  // to the answer phase.
  document.addEventListener('keydown', (e) => {
    if (state.screen !== 'game' || !state.running || state.paused) return;

    if (state.awaitingNext) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (typeof goNext === 'function') goNext();
      }
      return;
    }

    if (state.phase === 'read') {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (typeof beginAnswerPhase === 'function') beginAnswerPhase();
      }
      return;
    }

    const map = { '1': 0, '2': 1, '3': 2, '4': 3 };
    if (e.key in map) {
      const btn = document.querySelectorAll('.option')[map[e.key]];
      if (btn && !btn.disabled) submitAnswer(btn.dataset.optionId);
    }
  });
}

'use strict';

// DOM rendering for the three screens. Pure functions over `state` —
// no game-loop logic here.

function showScreen(name) {
  state.screen = name;
  document.querySelectorAll('.screen').forEach(el => {
    el.classList.toggle('active', el.id === `${name}-screen`);
  });
  // Wallet pill is mirrored across home + game; keep them in sync.
  const homeAuthed = !state.isGuest;
  document.getElementById('wallet-pill')?.classList.toggle('hidden', !homeAuthed);
  document.getElementById('wallet-pill-game')?.classList.toggle('hidden', !homeAuthed);

  if (name === 'home') renderHomePersonalBest();
}

// Show the device-local personal best on the home screen (hidden until the
// player has completed at least one quiz).
function renderHomePersonalBest() {
  const wrap = document.getElementById('home-pb');
  if (!wrap || typeof personalBest === 'undefined') return;
  const pb = personalBest.load();
  if (!pb.plays) { wrap.classList.add('hidden'); return; }
  document.getElementById('home-pb-points').textContent = String(pb.points);
  document.getElementById('home-pb-streak').textContent = String(pb.streak);
  wrap.classList.remove('hidden');
}

// Drive the answer-phase countdown ring: number + urgent (last-5s) state.
function renderCountdown(secs, urgent) {
  const num  = document.getElementById('countdown-num');
  const wrap = document.getElementById('countdown');
  if (num)  num.textContent = String(Math.max(0, secs | 0));
  if (wrap) wrap.classList.toggle('urgent', !!urgent);
}

// Drive the READ-phase draining bar: width = frac * 100% (1 → full, 0 → empty).
function renderReadProgress(frac) {
  const fill = document.getElementById('read-bar-fill');
  if (fill) fill.style.width = Math.max(0, Math.min(1, frac)) * 100 + '%';
}

// Floating "+N pts" that rises and fades after a correct answer.
function showPointsGain(pts) {
  const el = document.getElementById('points-gain');
  if (!el || (pts | 0) <= 0) return;
  el.textContent = `+${pts | 0} pts`;
  // Restart the animation even on back-to-back correct answers.
  el.classList.remove('show');
  void el.offsetWidth;                                 // force reflow
  el.classList.add('show');
}

function renderWallet() {
  const a = document.getElementById('wallet-balance');
  const b = document.getElementById('wallet-balance-game');
  const txt = `${wallet.balance} ${wallet.currency}`;
  if (a) a.textContent = txt;
  if (b) b.textContent = txt;
}

// Kahoot-style answer tiles: four colored shapes, tuned to the warm MuslimPro
// palette. Index 0–3 maps to triangle / diamond / circle / square.
const OPTION_SHAPES = [
  { cls: 'shape-triangle', glyph: '▲' },
  { cls: 'shape-diamond',  glyph: '◆' },
  { cls: 'shape-circle',   glyph: '●' },
  { cls: 'shape-square',   glyph: '■' },
];

// READ phase: paint the prompt only. Options stay hidden until the read bar
// expires (or the player taps to skip), at which point revealOptions() runs.
function renderQuestion(q, idx) {
  document.getElementById('question-text').textContent = q.prompt;

  // Badge shows position in the flat run, e.g. "Q 1 / 7".
  const total = state.questions.length || GAME_CONFIG.questionsPerRun;
  document.getElementById('q-badge').textContent = `Q ${idx + 1} / ${total}`;

  // Level indicator — colour-coded chip so the player always knows their pace.
  const chip = document.getElementById('level-chip');
  if (chip && state.level) {
    chip.textContent = state.level.charAt(0).toUpperCase() + state.level.slice(1);
    chip.classList.remove('hidden', 'lvl-beginner', 'lvl-intermediate', 'lvl-expert');
    chip.classList.add('lvl-' + state.level);
  }

  // Progress bar fills across the run, based on how many questions have been
  // *started*.
  const progressPct = Math.round((idx / total) * 100);
  document.getElementById('progress-bar').style.setProperty('--progress', progressPct + '%');

  // Read-phase UI: full read bar shown, answer ring + options hidden.
  document.getElementById('read-bar')?.classList.remove('hidden');
  renderReadProgress(1);
  document.getElementById('countdown')?.classList.add('hidden');

  // Reset countdown ring to full, clear any leftover urgent state.
  const bar = document.getElementById('countdown-ring-bar');
  if (bar) bar.style.strokeDashoffset = '0';

  hideFeedback();
  hideNextButton();

  // Options are emptied + hidden; revealOptions() builds them for the answer phase.
  const wrap = document.getElementById('options');
  wrap.innerHTML = '';
  wrap.classList.add('hidden');
}

// ANSWER phase: build + reveal the four shape tiles and swap the read bar for
// the answer-timer ring.
function revealOptions(q) {
  document.getElementById('read-bar')?.classList.add('hidden');
  document.getElementById('countdown')?.classList.remove('hidden');

  const wrap = document.getElementById('options');
  wrap.innerHTML = '';
  q.options.forEach((opt, i) => {
    const shape = OPTION_SHAPES[i % OPTION_SHAPES.length];
    const b = document.createElement('button');
    b.className = `option ${shape.cls}`;
    b.dataset.optionId = opt.id;
    b.style.setProperty('--reveal-delay', (i * 60) + 'ms');

    const glyph = document.createElement('span');
    glyph.className = 'option-shape';
    glyph.setAttribute('aria-hidden', 'true');
    glyph.textContent = shape.glyph;

    const label = document.createElement('span');
    label.className = 'option-label';
    label.textContent = opt.text;

    b.append(glyph, label);
    wrap.appendChild(b);
  });
  wrap.classList.remove('hidden');
}

// Sequential reveal: first lock in the user's pick + banner, then (if wrong
// or on timeout) highlight the correct answer after a short pause so the
// learner registers the feedback before seeing the truth.
function revealOption(optionId, correct, q) {
  document.querySelectorAll('.option').forEach(el => {
    el.disabled = true;
    if (el.dataset.optionId === optionId) {
      el.classList.add(correct ? 'correct' : 'wrong');
    }
  });
  showFeedback(correct ? 'correct' : 'wrong');

  if (correct) return;

  // After 500ms, glow the actual correct answer so the user can learn.
  setTimeout(() => {
    document.querySelectorAll('.option').forEach(el => {
      const opt = q.options.find(o => o.id === el.dataset.optionId);
      if (opt && opt.isCorrect && el.dataset.optionId !== optionId) {
        el.classList.add('correct');
      }
    });
  }, 500);
}

// Used by onTimeout(): no user pick to mark, just reveal correct + banner.
function revealCorrectOnly(q, reason) {
  showFeedback(reason || 'timeout');
  document.querySelectorAll('.option').forEach(el => {
    el.disabled = true;
    const opt = q.options.find(o => o.id === el.dataset.optionId);
    if (opt && opt.isCorrect) el.classList.add('correct');
  });
}

function showFeedback(kind) {
  const banner = document.getElementById('feedback-banner');
  const text   = document.getElementById('feedback-text');
  if (!banner || !text) return;
  banner.classList.remove('hidden', 'correct', 'wrong', 'timeout');
  banner.classList.add(kind);
  text.textContent =
    kind === 'correct' ? '✓ Correct' :
    kind === 'timeout' ? '⏱ Time\'s up — here\'s the answer' :
                         '✗ Not quite — here\'s the answer';
}

function hideFeedback() {
  const banner = document.getElementById('feedback-banner');
  if (banner) banner.classList.add('hidden');
}

function showNextButton() {
  const btn = document.getElementById('btn-next');
  if (!btn) return;
  btn.classList.remove('hidden');
  // Reflow + reveal animation, and pull focus so Enter/Space advances.
  void btn.offsetWidth;
  btn.classList.add('show');
  btn.focus();
}

function hideNextButton() {
  const btn = document.getElementById('btn-next');
  if (!btn) return;
  btn.classList.add('hidden');
  btn.classList.remove('show');
}

function updateScore() {
  // Score is intentionally hidden during play — the running total is only
  // revealed on the end screen via paintEndScreen(). This function is kept
  // as a no-op so main.js / quiz.js don't need conditional calls.
}

function updateStreak() {
  const pill  = document.getElementById('streak-pill');
  const count = document.getElementById('streak-count');
  if (!pill || !count) return;
  count.textContent = String(state.streak);
  // Always visible during play so the player can see their streak build from 0;
  // `cold` dims the flame until a streak is actually going.
  pill.classList.remove('hidden');
  pill.classList.toggle('cold', state.streak < 1);
  pill.classList.toggle('hot',  state.streak >= GAME_CONFIG.streakBonusAt);
}

function paintEndScreen() {
  // Denominator is the number of questions actually answered.
  const total   = state.answers.length || GAME_CONFIG.questionsPerRun;
  const correct = state.answers.filter(a => a.correct).length;
  const acc     = total ? Math.round((correct / total) * 100) : 0;

  document.getElementById('end-correct').textContent  = `${correct}/${total}`;
  document.getElementById('end-accuracy').textContent = `${acc}%`;
  document.getElementById('end-streak').textContent   = String(state.bestStreak);

  renderPersonalBest(acc);

  const row = document.getElementById('endPointsRow');
  row.classList.remove('hidden');

  const ptsEl    = document.getElementById('endPoints');
  const claimBtn = document.getElementById('claimBtn');

  if (state.isGuest) {
    ptsEl.textContent = `${state.totalPoints} pts pending`;
    claimBtn.classList.remove('hidden');
    claimBtn.disabled = false;
    claimBtn.textContent = 'Sign in to claim points';
  } else {
    // Authed users will see "+N pts" once /complete returns.
    ptsEl.textContent = `+${state.totalPoints} pts`;
    claimBtn.classList.add('hidden');
  }
}

// Fold this run into the device-local personal best, then show the bar to beat
// plus a "new best" banner if any record fell. Skips recording when we're just
// re-painting a restored snapshot (it was already counted on the original run).
function renderPersonalBest(acc) {
  const pbEl     = document.getElementById('end-pb');
  const newBest  = document.getElementById('end-newbest');
  if (!pbEl || typeof personalBest === 'undefined') return;

  let pb, beat = { points: false, streak: false, accuracy: false };
  if (state.restoredFromSnapshot) {
    pb = personalBest.load();
  } else {
    const res = personalBest.record({
      points:   state.totalPoints,
      streak:   state.bestStreak,
      accuracy: acc,
    });
    pb   = res.pb;
    beat = res.beat;
  }

  document.getElementById('end-pb-points').textContent = String(pb.points);
  document.getElementById('end-pb-streak').textContent = String(pb.streak);
  pbEl.classList.remove('hidden');

  const isNewBest = beat.points || beat.streak || beat.accuracy;
  newBest?.classList.toggle('hidden', !isNewBest);
}

function paintClaimResult(pointsEarned) {
  const ptsEl    = document.getElementById('endPoints');
  const claimBtn = document.getElementById('claimBtn');
  if (ptsEl)    ptsEl.textContent = `+${pointsEarned} pts`;
  if (claimBtn) claimBtn.classList.add('hidden');
}

function showClaimCta() {
  const claimBtn = document.getElementById('claimBtn');
  if (claimBtn) {
    claimBtn.classList.remove('hidden');
    claimBtn.disabled = false;
  }
  document.getElementById('endPointsRow')?.classList.remove('hidden');
}

function setClaimBusy(busy) {
  const claimBtn = document.getElementById('claimBtn');
  if (!claimBtn) return;
  claimBtn.disabled = !!busy;
  claimBtn.textContent = busy ? 'Claiming…' : 'Sign in to claim points';
}

function restoreFromSnapshot(snap) {
  state.restoredFromSnapshot = true;
  state.sessionId   = snap.sessionId || null;
  state.events      = Array.isArray(snap.events) ? snap.events.slice() : [];
  state.totalPoints = snap.stats?.totalPoints | 0;
  state.bestStreak  = snap.stats?.bestStreak  | 0;
  // Fake the answers/questions arrays so paintEndScreen can compute display.
  const correct = snap.stats?.correct | 0;
  const total   = snap.stats?.total   | 0;
  state.questions = new Array(total).fill(null).map((_, i) => ({ id: 'r' + i }));
  state.answers   = new Array(total).fill(null).map((_, i) => ({
    qId: 'r' + i, selectedId: null, correct: i < correct, msToAnswer: 0,
  }));
  paintEndScreen();
  showScreen('end');
}

'use strict';

// Lifecycle: startGame → quiz.js drives questions → endGame → claimPoints.
// Mirrors rami/puzzle's main.js layout.

async function startGame(level) {
  // Reset session-local state.
  state.level         = (level && GAME_CONFIG.levels[level]) ? level : GAME_CONFIG.defaultLevel;
  state.questions     = [];
  state.qIdx          = 0;
  state.phase         = 'read';
  state.readStartT    = 0;
  state.answerStartT  = 0;
  state.pausedAt      = 0;
  state.pendingAdvance = false;
  state.answers       = [];
  state.totalPoints   = 0;
  state.streak        = 0;
  state.bestStreak    = 0;
  state.events        = [];
  state.sessionId     = null;
  state.sessionStartT = performance.now();
  state.running       = true;
  state.paused        = false;

  try {
    state.questions = await buildQuestionPool();
    if (!state.questions.length) throw new Error('no questions available');
  } catch (e) {
    console.warn('[islamic-quiz] question fetch failed:', e);
    state.running = false;
    return;
  }

  // Authed users open a server-side session so the eventual /complete call
  // has a sessionId to attach to. Guests skip this — claim at end opens a
  // single-shot /submit with the events array.
  if (!state.isGuest) {
    try {
      const s = await api.startSession(GAME_CONFIG.mode);
      state.sessionId = s.sessionId || null;
    } catch (e) {
      console.warn('[islamic-quiz] startSession failed:', e);
      // Continue anyway — claim will fall back to /submit if needed.
    }
  }

  sendToHost('game:start', { game: 'islamic-quiz', mode: GAME_CONFIG.mode });
  sendToHost('analytics:event', {
    name: 'islamic_quiz_session_started',
    properties: { mode: GAME_CONFIG.mode, level: state.level, isGuest: state.isGuest },
  });

  updateScore();
  updateStreak();
  showScreen('game');
  startQuestion(0);
}

// Build one flat run: fetch every difficulty in parallel, merge, shuffle, and
// slice to questionsPerRun. The pool is the same regardless of level — only
// the answer timer changes — so we ask each difficulty for its share and top
// up from whatever came back. A failed difficulty fetch is tolerated as long
// as at least one returns questions.
async function buildQuestionPool() {
  const diffs   = GAME_CONFIG.difficulties;
  const perDiff = Math.ceil(GAME_CONFIG.questionsPerRun / diffs.length) + 1;

  const settled = await Promise.allSettled(
    diffs.map(d => api.fetchTier(d, perDiff))
  );
  const pool = [];
  for (const r of settled) {
    if (r.status === 'fulfilled' && r.value && Array.isArray(r.value.questions)) {
      pool.push(...r.value.questions);
    }
  }
  shuffle(pool);
  return pool.slice(0, GAME_CONFIG.questionsPerRun);
}

// Fisher–Yates in place.
function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function endGame() {
  paintEndScreen();
  showScreen('end');
  sendToHost('analytics:event', {
    name: 'islamic_quiz_session_completed',
    properties: {
      correct:     state.answers.filter(a => a.correct).length,
      total:       state.questions.length,
      points:      state.totalPoints,
      bestStreak:  state.bestStreak,
      isGuest:     state.isGuest,
    },
  });

  // Authed users with a sessionId: credit immediately. Guests: claim CTA.
  if (!state.isGuest && state.sessionId) {
    claimPoints();
  }
}

async function claimPoints() {
  if (state.isGuest) {
    // Persist before OAuth so a process kill mid-flow lands the user back
    // on the end screen with a pending claim.
    snapshot.save(snapshot.buildPostGame(state));
    setClaimBusy(true);
    try {
      await api.requestLogin();
    } catch (_) {
      setClaimBusy(false);
      return;
    }
    // After auth:ready, wallet re-init fires, then we submit.
    state.isGuest = false;
    try {
      const res = await api.submitSession(GAME_CONFIG.mode, state.events, state.totalPoints);
      state.sessionId = res.sessionId || state.sessionId;
      wallet.balance += res.pointsEarned | 0;
      paintClaimResult(res.pointsEarned | 0);
      renderWallet();
      snapshot.clear();
      sendToHost('game:complete', { game: 'islamic-quiz', mode: GAME_CONFIG.mode });
    } catch (e) {
      console.warn('[islamic-quiz] submit failed:', e);
      setClaimBusy(false);
    }
    return;
  }

  // Authed path — completeSession against the open session.
  try {
    const res = await api.completeSession(state.sessionId, state.events, state.totalPoints);
    wallet.balance += res.pointsEarned | 0;
    paintClaimResult(res.pointsEarned | 0);
    renderWallet();
    sendToHost('game:complete', { game: 'islamic-quiz', mode: GAME_CONFIG.mode });
  } catch (e) {
    console.warn('[islamic-quiz] complete failed:', e);
  }
}

// Pause/resume freeze the running timers and give the paused time back, so a
// native backgrounding pause (game:pause / game:resume) never costs the player
// answer time. The intervals keep firing but no-op while paused; on resume we
// shift the start references forward by however long we were paused.
function pauseGame() {
  if (state.paused || !state.running) return;
  state.paused  = true;
  state.pausedAt = performance.now();
}

function resumeGame() {
  if (!state.paused) return;
  state.paused = false;
  if (state.pausedAt) {
    const delta = performance.now() - state.pausedAt;
    if (state.readStartT)   state.readStartT   += delta;
    if (state.answerStartT) state.answerStartT += delta;
    state.pausedAt = 0;
  }
  // Replay a correct-answer auto-advance that was deferred while paused.
  if (state.pendingAdvance) {
    state.pendingAdvance = false;
    if (typeof advance === 'function') advance();
  }
}

// Tear the run down and return to the home screen. No web button calls this —
// leaving the game is the native back button's job — but it stays as the
// handler for the native `game:exit` lifecycle event (see wallet.js) and the
// end-screen "Menu" button.
function exitToMenu() {
  // If the user backs out mid-quiz with an open session, let the backend
  // know so it can close the row and refund any held credit.
  if (state.running && state.sessionId && !state.isGuest) {
    api.abandonSession(state.sessionId).catch(() => {});
  }
  state.running = false;
  state.awaitingNext = false;
  if (typeof hideNextButton === 'function') hideNextButton();
  if (typeof stopCountdown === 'function') stopCountdown();
  showScreen('home');
  sendToHost('game:exit', { game: 'islamic-quiz' });
}

function boot() {
  bindInput();
  showScreen('home');
  initWallet();
  sendToHost('analytics:event', { name: 'islamic_quiz_loaded' });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
